import moment from 'moment';
import { zonoApi } from '../api/zonoApi';
import {
  AddItemToActiveOrderRequest,
  AddItemToActiveOrderResponse,
  Audit,
  CheckoutOrder,
  DeletePoFileRequest,
  DeletePoFileResponse,
  OrderCheckoutRequest,
  OrderDetailsRequest,
  OrderDetailsResponse,
  OrderLogsRequest,
  OrderLogsResponse,
  OrderLogsTypes,
  PoOrderDetailsResponse,
  ProductMappingRequest,
  ProductMappingResponse,
  SuggestMappingRequest,
  SuggestedProducts,
  TrackPODetailsRequest,
  EditOrderRequest,
  EditOrderResponse,
  PoListOrderRequest,
  PoListOrderResponse,
  POFileDetailsByIdResponse,
  POFileDetailsByIdRequest,
  GetCartRequest,
  GetCartSummaryResponse,
  GetCartSummaryRequest,
  DeleteLinesRequest,
  RemovedOrderPromotionRequest,
  EmbeddedSignupCallbackResponse,
  EmbeddedSignupCallbackRequest,
  ListOrderRequest,
  ListOrderResponse,
  CancelOrderRequest,
  UpdatePOStatusRequest,
  AcceptOrderRequest,
  OrderFilterResponse,
  OrderFilterRequest,
  CreateOrderNoteRequest,
  GetOrderLevelSchemesRequest,
  GetOrderLevelSchemesResponse,
  LedgerSummaryReportsResponse,
  LedgerSummaryReportsRequest,
} from './order-model';
import {
  FilterRoute,
  FilterValue,
  ListFilterKey,
  ListFiltersMap,
  updateFiltersByRoute,
} from '../filter';

export interface NotifyToBuyerRequest {
  sellerWorkspaceId: string;
  poFileId: string;
  customerId: string;
}

export const OrderApi = zonoApi.injectEndpoints({
  endpoints: (builder) => ({
    listOrders: builder.mutation<ListOrderResponse, ListOrderRequest>({
      query: (args) => ({
        url: `hub/commerce-v2/orders`,
        method: 'POST',
        body: args.body,
        params: {
          ...args.params,
          sellerWorkspaceId: args.params.workspaceId,
        },
      }),
    }),
    ordersFilters: builder.mutation<OrderFilterResponse, OrderFilterRequest>({
      query: (args) => ({
        url: `hub/commerce-v2/order-filter?sellerWorkspaceId=${args.workspaceId}`,
        method: 'POST',
        body: args,
      }),
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const cfaValues: FilterValue[] = (data.fulfillments || []).map(
            (item) => ({
              id: item?.cfaIds,
              label: item?.cfaName,
              selected: false,
            })
          );

          const tagValues: FilterValue[] = (data.tags || []).map((item) => ({
            id: item.tagId,
            label: item.tagName,
            selected: false,
          }));

          const listFilter: Partial<ListFiltersMap> = {};
          if (cfaValues.length > 0) {
            listFilter[ListFilterKey.Cfa] = {
              multiSelect: true,
              values: cfaValues,
            };
          }
          if (tagValues.length > 0) {
            listFilter[ListFilterKey.OrderTags] = {
              multiSelect: true,
              values: tagValues,
            };
          }

          dispatch(
            updateFiltersByRoute({
              route: FilterRoute.Order,
              filter: {
                listFilter,
              },
            })
          );
        } catch (err) {
          console.log(err, 'dispatchErr');
        }
      },
    }),
    getOrderDetails: builder.mutation<
      OrderDetailsResponse,
      OrderDetailsRequest
    >({
      query: (args) => ({
        url: `hub/commerce-v2/orders/details/${args.workspaceId}/${args.orderId}?includeInvoice=true&sellerWorkspaceId=${args.workspaceId}`,
        method: 'POST',
        body: args.body,
        params: { orderId: args.orderId },
      }),
    }),
    listCart: builder.query<
      PoListOrderResponse,
      { body: PoListOrderRequest; workspaceId: string }
    >({
      query: (args) => ({
        url: `hub/commerce-v2/poFiles/${args.workspaceId}?sellerWorkspaceId=${args.workspaceId}`,
        method: 'GET',
        params: { ...args.body, random: Math.random() },
      }),
    }),
    getTrackPODetails: builder.mutation<
      PoOrderDetailsResponse,
      TrackPODetailsRequest
    >({
      query: (args) => ({
        url: `hub/commerce-v2/pofiles/details/${args.workspaceId}?sellerWorkspaceId=${args.workspaceId}`,
        method: 'POST',
        body: args.body,
        params: { orderId: args.orderId },
      }),
    }),
    getOrdersLogs: builder.mutation<
      { [key: string]: Audit[] },
      OrderLogsRequest
    >({
      query: (args) => ({
        url: `hub/commerce-v2/order-logs?sellerWorkspaceId=${args.workspaceId}`,
        method: 'POST',
        body: args,
      }),
      transformResponse: (res: OrderLogsResponse[]) => {
        const formatTime = (inputDateString: string) => {
          const inputDate = new Date(inputDateString);
          const formattedTime = inputDate.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
          });
          return formattedTime;
        };

        const audits = res
          ?.map((obj) => {
            const data = obj.data?.map((x) => {
              if (x?.action === OrderLogsTypes.REMAPPED) {
                return {
                  ...x,
                  sequence: obj.sequence,
                  productName: x.updatedFields?.productDetails?.name,
                  sku: x.updatedFields?.productDetails?.sku,
                  oldProductName: x.updatedFields?.oldProductDetails?.name,
                  oldSku: x.updatedFields?.oldProductDetails?.sku,
                };
              } else {
                return {
                  ...x,
                  sequence: obj.sequence,
                  productName:
                    obj.productDetails?.name ?? obj?.productDetails ?? '-',
                  sku: obj?.productDetails?.sku ?? '-',
                };
              }
            });
            return { ...obj, data: data };
          })
          ?.map((data) => data?.data)
          ?.flat()
          ?.sort(
            (a, b) =>
              new Date(b.timestamp).getTime() -
                new Date(a.timestamp).getTime() ||
              parseInt(b.timestamp.slice(-3)) - parseInt(a.timestamp.slice(-3))
          )
          .map((c) => ({
            ...c,
            date: moment(new Date(c?.timestamp))
              .format('Do MMM, YYYY')
              ?.toUpperCase(),
            time: formatTime(c?.timestamp),
          }));
        const groupedAudits = audits.reduce((acc: any, current) => {
          const date = current.date;
          if (!acc[date]) {
            acc[date] = [];
          }
          acc[date].push(current);
          return acc;
        }, {});

        return groupedAudits;
      },
    }),
    addItemToActiveOrder: builder.mutation<
      AddItemToActiveOrderResponse,
      AddItemToActiveOrderRequest
    >({
      query: (args) => ({
        url: `hub/commerce-v2/orders/additemtoactiveorder/${args.sellerWorkspaceId}?sellerWorkspaceId=${args.sellerWorkspaceId}`,
        method: 'POST',
        body: { ...args, random: Math.random() },
      }),
    }),
    deletePoFile: builder.mutation<DeletePoFileResponse, DeletePoFileRequest>({
      query: (args) => {
        const { workspaceId, poFileImportSource, ...otherParams } = args;
        return {
          url: `hub/commerce-v2/poFile/${workspaceId}?sellerWorkspaceId=${args.workspaceId}`,
          method: 'DELETE',
          body: otherParams,
        };
      },
    }),
    checkoutOrders: builder.mutation<
      { status: string; statusCode: number; orders: CheckoutOrder[] },
      OrderCheckoutRequest
    >({
      query(arg) {
        const { sellerWorkspaceId } = arg;
        return {
          url: `hub/commerce-v2/orders/checkout/${sellerWorkspaceId}`,
          method: 'POST',
          body: arg,
          params: {
            sellerWorkspaceId: sellerWorkspaceId,
          },
        };
      },
    }),
    editOrder: builder.mutation<EditOrderResponse, EditOrderRequest>({
      query: (args) => {
        const { workspaceId, body } = args;
        return {
          url: `hub/commerce-v2/orders/edit/${workspaceId}`,
          method: 'POST',
          body: body,
          params: {
            sellerWorkspaceId: workspaceId,
          },
        };
      },
    }),
    suggestedMapping: builder.mutation<
      SuggestedProducts[],
      SuggestMappingRequest
    >({
      query(arg) {
        const { workspaceId, ...params } = arg;
        return {
          url: `hub/commerce-v2/poFile/line/available-mapping/${workspaceId}?sellerWorkspaceId=${arg.workspaceId}`,
          method: 'GET',
          params: params,
        };
      },
    }),

    mapProduct: builder.mutation<ProductMappingResponse, ProductMappingRequest>(
      {
        query(arg) {
          const { inviteId, ...body } = arg;
          return {
            url: `/hub/commerce-v2/poFile/map/${inviteId}?sellerWorkspaceId=${arg.workspaceId}`,
            method: 'POST',
            body: body,
          };
        },
      }
    ),
    notifyToBuyer: builder.mutation<void, NotifyToBuyerRequest>({
      query(arg) {
        const { sellerWorkspaceId, ...body } = arg;
        return {
          url: `hub/commerce-v2/orders/notify-to-buyer/${sellerWorkspaceId}?sellerWorkspaceId=${arg.sellerWorkspaceId}`,
          method: 'POST',
          body,
        };
      },
    }),

    // Works for manual POs as well
    poFileDetailsById: builder.query<
      POFileDetailsByIdResponse,
      POFileDetailsByIdRequest
    >({
      query(arg) {
        return {
          url: `/hub/commerce-v2/poFile/${arg?.poFileId}/${arg?.workspaceId}`,
          method: 'GET',
          params: arg?.params,
        };
      },
    }),

    getCart: builder.query<PoListOrderResponse, GetCartRequest>({
      query(arg) {
        return {
          url: `/hub/commerce-v2/${arg?.params?.sellerWorkspaceId}/cart`,
          method: 'GET',
          params: { ...arg?.params, random: Math.random() },
        };
      },
    }),

    getCartSummary: builder.query<
      GetCartSummaryResponse,
      GetCartSummaryRequest
    >({
      query: (args) => ({
        url: `hub/commerce-v2/poFiles/summary/${args.sellerWorkspaceId}`,
        method: 'GET',
        params: {
          customerId: args.customerId,
          sellerWorkspaceId: args.sellerWorkspaceId,
        },
      }),
    }),

    deleteFromActiveOrder: builder.mutation<
      { error: string[]; msg: string },
      DeleteLinesRequest
    >({
      query(arg) {
        return {
          url: `hub/commerce-v2/orders/deleteLines/${arg?.body?.sellerWorkspaceId}`,
          method: 'POST',
          body: arg?.body,
          params: arg?.params,
        };
      },
    }),
    removeOrderPromotion: builder.mutation<
      { error?: string[]; msg?: string },
      RemovedOrderPromotionRequest
    >({
      query(arg) {
        return {
          url: `hub/commerce-v2/orders/${arg.workspaceId}/${arg.orderId}/promotion/${arg.promotionId}`,
          params: {
            sellerWorkspaceId: arg?.workspaceId,
          },
          method: 'DELETE',
        };
      },
    }),

    embeddedSignupCallback: builder.mutation<
      EmbeddedSignupCallbackResponse,
      EmbeddedSignupCallbackRequest
    >({
      query: (args) => ({
        url: `hub/orgs/api/whatsapp-config/embedded-signup-webhook`,
        method: 'POST',
        body: args,
        params: {
          sellerWorkspaceId: args.sellerWorkspaceId,
        },
      }),
    }),

    updateDiscountAndPriceOrder: builder.mutation<
      EditOrderResponse,
      EditOrderRequest
    >({
      query: (payload) => ({
        url: `hub/commerce-v2/orders/update/${payload.workspaceId}`,
        method: 'POST',
        body: payload.body,
        params: {
          sellerWorkspaceId: payload.workspaceId,
        },
      }),
    }),

    cancelOrder: builder.mutation<any, CancelOrderRequest>({
      query(arg) {
        const { orderId, sellerWorkspaceId, ...body } = arg;
        return {
          url: `hub/commerce-v2/${sellerWorkspaceId}/order/cancel/${orderId}`,
          method: 'POST',
          body: body,
          params: {
            sellerWorkspaceId: sellerWorkspaceId,
          },
        };
      },
    }),
    updatePOStatus: builder.mutation<any, UpdatePOStatusRequest>({
      query(arg) {
        const { sellerWorkspaceId, ...body } = arg;
        return {
          url: `hub/commerce-v2/poFile/order/${sellerWorkspaceId}`,
          method: 'PUT',
          body,
          params: {
            sellerWorkspaceId: sellerWorkspaceId,
          },
        };
      },
    }),

    acceptOrder: builder.mutation<
      any,
      { body: AcceptOrderRequest[]; workspaceId: string }
    >({
      query: (args) => ({
        url: `hub/commerce-v2/orders/${args.workspaceId}`,
        method: 'POST',
        body: args.body,
        params: {
          sellerWorkspaceId: args.workspaceId,
        },
      }),
    }),
    getOrderNote: builder.query<any, { sellerWorkspaceId: string }>({
      query(args) {
        return {
          url: `hub/commerce-v2/orders/metaSchema/${args.sellerWorkspaceId}`,
          method: 'GET',
          params: args,
        };
      },
    }),
    createOrderNote: builder.mutation<any, CreateOrderNoteRequest>({
      query(args) {
        return {
          url: `hub/commerce-v2/orders/metaSchema/${args.sellerWorkspaceId}`,
          method: 'POST',
          body: args.body,
          params: args.params,
        };
      },
    }),
    getOrderLevelSchemes: builder.mutation<
      GetOrderLevelSchemesResponse,
      GetOrderLevelSchemesRequest
    >({
      query(arg) {
        const { sellerWorkspaceId, ...other } = arg;
        return {
          url: `hub/commerce-v2/scheme/customer/hitl/${sellerWorkspaceId}`,
          method: 'POST',
          body: other,
          params: {
            sellerWorkspaceId: sellerWorkspaceId,
          },
        };
      },
    }),
    ledgerSummaryReport: builder.mutation<
      LedgerSummaryReportsResponse,
      LedgerSummaryReportsRequest
    >({
      query(arg) {
        return {
          url: `/hub/reports/api/v2/ledger-summary-report`,
          method: 'POST',
          body: arg,
        };
      },
    }),
  }),
});

export const {
  useGetOrderDetailsMutation,
  useGetTrackPODetailsMutation,
  useRemoveOrderPromotionMutation,
  useGetOrdersLogsMutation,
  useAddItemToActiveOrderMutation,
  useDeletePoFileMutation,
  useCheckoutOrdersMutation,
  useEditOrderMutation,
  useSuggestedMappingMutation,
  useMapProductMutation,
  useNotifyToBuyerMutation,
  useLazyListCartQuery,
  useLazyPoFileDetailsByIdQuery,
  useLazyGetCartQuery,
  useLazyGetCartSummaryQuery,
  useDeleteFromActiveOrderMutation,
  useEmbeddedSignupCallbackMutation,
  useListOrdersMutation,
  useUpdateDiscountAndPriceOrderMutation,
  useCancelOrderMutation,
  useUpdatePOStatusMutation,
  useAcceptOrderMutation,
  useOrdersFiltersMutation,
  useLazyGetOrderNoteQuery,
  useCreateOrderNoteMutation,
  useGetOrderLevelSchemesMutation,
  useLedgerSummaryReportMutation,
} = OrderApi;
